/**
 * Affiliate Requests Service
 * Handles product access requests, wishlist management, and request status updates
 */

import { supabase } from '../supabaseClient';

export interface ProductRequest {
  id: string;
  creator_id: string;
  brand_id: string;
  product_id: number;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  requested_at: string;
  reviewed_at?: string;
  reviewed_by?: string;
  creator_message?: string;
  brand_response?: string;
  product?: {
    product_name: string;
    price: number;
    main_images: string[];
    default_commission_rate: number;
    default_commission_type: 'percentage' | 'fixed';
  };
  affiliate_settings?: {
    start_date?: string;
    end_date?: string;
    max_uses?: number;
    current_uses?: number;
  };
  brand?: {
    nick_name?: string;
    store_name: string;
    profile_image?: string;
  };
  creator?: {
    nick_name?: string;
    store_name: string;
    profile_image?: string;
    social_links?: {
      instagram?: string;
      tiktok?: string;
      youtube?: string;
      website?: string;
    };
  };
}

export interface WishlistItem {
  id: string;
  creator_id: string;
  product_id: number;
  added_at: string;
  notes?: string;
  product?: {
    product_name: string;
    price: number;
    main_images: string[];
    default_commission_rate: number;
    default_commission_type: 'percentage' | 'fixed';
    seller_id: string;
    // Affiliate settings
    affiliate_start_date?: string;
    affiliate_end_date?: string;
    affiliate_max_uses?: number;
    affiliate_current_uses?: number;
  };
}

/**
 * Create a product access request from creator to brand
 * @param creatorId - The creator's seller_profile ID
 * @param productId - The product ID to request access to
 * @param message - Optional message from creator to brand
 */
export async function createProductRequest(
  creatorId: string,
  productId: number,
  message?: string
): Promise<{ data: any; error: any }> {
  try {
    // First, get the product's brand_id
    const { data: product, error: productError } = await supabase
      .from('products')
      .select('seller_id')
      .eq('product_id', productId)
      .single();

    if (productError || !product) {
      console.error('Error fetching product:', productError);
      return { data: null, error: productError || new Error('Product not found') };
    }

    // Create the request
    const { data, error } = await supabase
      .from('affiliate_product_requests')
      .insert({
        creator_id: creatorId,
        brand_id: product.seller_id,
        product_id: productId,
        creator_message: message || null,
        status: 'pending',
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating product request:', error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (err) {
    console.error('Unexpected error in createProductRequest:', err);
    return { data: null, error: err };
  }
}

/**
 * Bulk create product requests from wishlist items
 * @param creatorId - The creator's seller_profile ID
 * @param productIds - Array of product IDs to request
 * @param message - Optional message for all requests
 */
export async function bulkCreateProductRequests(
  creatorId: string,
  productIds: number[],
  message?: string
): Promise<{ successCount: number; failedCount: number; error: any }> {
  try {
    let successCount = 0;
    let failedCount = 0;

    for (const productId of productIds) {
      const { error } = await createProductRequest(creatorId, productId, message);
      if (error) {
        failedCount++;
      } else {
        successCount++;
      }
    }

    return { successCount, failedCount, error: null };
  } catch (err) {
    console.error('Unexpected error in bulkCreateProductRequests:', err);
    return { successCount: 0, failedCount: productIds.length, error: err };
  }
}

/**
 * Get all product requests for a creator
 * @param creatorId - The creator's seller_profile ID
 * @param status - Optional status filter
 */
export async function getCreatorRequests(
  creatorId: string,
  status?: 'pending' | 'approved' | 'rejected' | 'cancelled'
): Promise<{ data: ProductRequest[] | null; error: any }> {
  try {
    let query = supabase
      .from('affiliate_product_requests')
      .select('*')
      .eq('creator_id', creatorId)
      .order('requested_at', { ascending: false });

    if (status) {
      query = query.eq('status', status);
    }

    const { data: requests, error: requestsError } = await query;

    if (requestsError) {
      console.error('Error fetching creator requests:', requestsError);
      return { data: null, error: requestsError };
    }

    // Filter out self-requests (where creator is also the brand)
    const filteredRequests = (requests || []).filter(r => r.creator_id !== r.brand_id);

    if (!filteredRequests || filteredRequests.length === 0) {
      return { data: [], error: null };
    }

    // Fetch product and brand info
    const productIds = filteredRequests.map((r) => r.product_id);
    const brandIds = Array.from(new Set(filteredRequests.map((r) => r.brand_id)));

    const { data: products, error: productsError } = await supabase
      .from('products')
      .select('product_id, product_name, price, main_images')
      .in('product_id', productIds);

    // Fetch commission info and affiliate settings from product_affiliate_settings
    const { data: commissionSettings, error: commissionError } = await supabase
      .from('product_affiliate_settings')
      .select('product_id, commission_rate, commission_type, start_date, end_date, max_uses, current_uses')
      .eq('is_public', true)
      .eq('status', 'approved')
      .in('product_id', productIds);

    const { data: brands, error: brandsError } = await supabase
      .from('seller_profile_public')
      .select('id, nick_name, store_name, profile_image')
      .in('id', brandIds);

    if (productsError || brandsError || commissionError) {
      console.error('Error fetching related data:', { productsError, brandsError, commissionError });
    }

    // Create commission and affiliate settings map
    const commissionMap = new Map(
      (commissionSettings || []).map((s) => [s.product_id, {
        default_commission_rate: s.commission_rate,
        default_commission_type: s.commission_type,
        start_date: s.start_date,
        end_date: s.end_date,
        max_uses: s.max_uses,
        current_uses: s.current_uses
      }])
    );

    // Create maps for quick lookup and combine with commission data
    const productsMap = new Map(
      (products || []).map((p) => [
        p.product_id,
        {
          ...p,
          default_commission_rate: commissionMap.get(p.product_id)?.default_commission_rate || 10,
          default_commission_type: commissionMap.get(p.product_id)?.default_commission_type || 'percentage'
        }
      ])
    );
    const brandsMap = new Map((brands || []).map((b) => [b.id, b]));

    // Combine data
    const combinedRequests: ProductRequest[] = filteredRequests.map((request) => ({
      ...request,
      product: productsMap.get(request.product_id),
      brand: brandsMap.get(request.brand_id),
      affiliate_settings: commissionMap.get(request.product_id) ? {
        start_date: commissionMap.get(request.product_id)?.start_date,
        end_date: commissionMap.get(request.product_id)?.end_date,
        max_uses: commissionMap.get(request.product_id)?.max_uses,
        current_uses: commissionMap.get(request.product_id)?.current_uses
      } : undefined,
    }));

    return { data: combinedRequests, error: null };
  } catch (err) {
    console.error('Unexpected error in getCreatorRequests:', err);
    return { data: null, error: err };
  }
}

/**
 * Get all product requests for a brand (seller)
 * @param brandId - The brand's seller_profile ID
 * @param status - Optional status filter
 */
export async function getBrandRequests(
  brandId: string,
  status?: 'pending' | 'approved' | 'rejected' | 'cancelled'
): Promise<{ data: ProductRequest[] | null; error: any }> {
  try {
    console.log('=== getBrandRequests 시작 ===');
    console.log('brandId:', brandId);
    console.log('status filter:', status);

    let query = supabase
      .from('affiliate_product_requests')
      .select('*')
      .eq('brand_id', brandId)
      .order('requested_at', { ascending: false });

    if (status) {
      query = query.eq('status', status);
    }

    const { data: requests, error: requestsError } = await query;

    if (requestsError) {
      console.error('Error fetching brand requests:', requestsError);
      return { data: null, error: requestsError };
    }

    console.log('초기 요청 개수:', requests?.length || 0);

    // Filter out self-requests (where creator is also the brand)
    const filteredRequests = (requests || []).filter(r => r.creator_id !== r.brand_id);

    console.log('자신에게 보낸 요청 제외 후:', filteredRequests.length);

    if (!filteredRequests || filteredRequests.length === 0) {
      return { data: [], error: null };
    }

    // Fetch product info to verify ownership
    const productIds = filteredRequests.map((r) => r.product_id);
    const creatorIds = Array.from(new Set(filteredRequests.map((r) => r.creator_id)));

    const { data: products } = await supabase
      .from('products')
      .select('product_id, product_name, price, main_images, seller_id')
      .in('product_id', productIds);

    // Fetch commission info and affiliate settings from product_affiliate_settings
    const { data: commissionSettings } = await supabase
      .from('product_affiliate_settings')
      .select('product_id, commission_rate, commission_type, start_date, end_date, max_uses, current_uses')
      .eq('is_public', true)
      .eq('status', 'approved')
      .in('product_id', productIds);

    const { data: creators } = await supabase
      .from('seller_profile_public')
      .select('id, nick_name, store_name, profile_image, social_links')
      .in('id', creatorIds);

    // Create commission and affiliate settings map
    const commissionMap = new Map(
      (commissionSettings || []).map((s) => [s.product_id, {
        default_commission_rate: s.commission_rate,
        default_commission_type: s.commission_type,
        start_date: s.start_date,
        end_date: s.end_date,
        max_uses: s.max_uses,
        current_uses: s.current_uses
      }])
    );

    // Create maps for quick lookup and combine with commission data
    const productsMap = new Map(
      (products || []).map((p) => [
        p.product_id,
        {
          ...p,
          default_commission_rate: commissionMap.get(p.product_id)?.default_commission_rate || 10,
          default_commission_type: commissionMap.get(p.product_id)?.default_commission_type || 'percentage'
        }
      ])
    );
    const creatorsMap = new Map((creators || []).map((c) => [c.id, c]));

    // Filter requests to only show products owned by this brand
    console.log('=== 상품 소유권 필터링 시작 ===');
    const ownedRequests = filteredRequests.filter((request) => {
      const product = productsMap.get(request.product_id);
      const isOwned = product && product.seller_id === brandId;

      console.log('Request 필터링:', {
        request_id: request.id,
        creator_id: request.creator_id,
        brand_id: request.brand_id,
        product_id: request.product_id,
        product_seller_id: product?.seller_id,
        current_brand_id: brandId,
        is_owned: isOwned,
        should_show: isOwned ? '✅ 표시' : '❌ 숨김',
      });

      return isOwned;
    });

    console.log('최종 필터링 후 요청 개수:', ownedRequests.length);

    // Combine data
    const combinedRequests: ProductRequest[] = ownedRequests.map((request) => ({
      ...request,
      product: productsMap.get(request.product_id),
      creator: creatorsMap.get(request.creator_id),
      affiliate_settings: commissionMap.get(request.product_id) ? {
        start_date: commissionMap.get(request.product_id)?.start_date,
        end_date: commissionMap.get(request.product_id)?.end_date,
        max_uses: commissionMap.get(request.product_id)?.max_uses,
        current_uses: commissionMap.get(request.product_id)?.current_uses
      } : undefined,
    })) as any;

    return { data: combinedRequests, error: null };
  } catch (err) {
    console.error('Unexpected error in getBrandRequests:', err);
    return { data: null, error: err };
  }
}

/**
 * Update a product request status (approve, reject, cancel)
 * @param requestId - The request ID
 * @param newStatus - The new status
 * @param response - Optional response message from brand
 */
export async function updateRequestStatus(
  requestId: string,
  newStatus: 'approved' | 'rejected' | 'cancelled',
  response?: string
): Promise<{ data: any; error: any }> {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    const updateData: any = {
      status: newStatus,
      reviewed_at: new Date().toISOString(),
      reviewed_by: user?.id || null,
    };

    if (response) {
      updateData.brand_response = response;
    }

    const { data, error } = await supabase
      .from('affiliate_product_requests')
      .update(updateData)
      .eq('id', requestId)
      .select()
      .single();

    if (error) {
      console.error('Error updating request status:', error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (err) {
    console.error('Unexpected error in updateRequestStatus:', err);
    return { data: null, error: err };
  }
}

/**
 * Add product to wishlist
 * @param creatorId - The creator's seller_profile ID
 * @param productId - The product ID
 * @param notes - Optional notes about why the creator wants this product
 */
export async function addToWishlist(
  creatorId: string,
  productId: number,
  notes?: string
): Promise<{ data: any; error: any }> {
  try {
    const { data, error } = await supabase
      .from('affiliate_wishlist')
      .insert({
        creator_id: creatorId,
        product_id: productId,
        notes: notes || null,
      })
      .select()
      .single();

    if (error) {
      console.error('Error adding to wishlist:', error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (err) {
    console.error('Unexpected error in addToWishlist:', err);
    return { data: null, error: err };
  }
}

/**
 * Remove product from wishlist
 * @param wishlistItemId - The wishlist item ID
 */
export async function removeFromWishlist(
  wishlistItemId: string
): Promise<{ error: any }> {
  try {
    const { error } = await supabase
      .from('affiliate_wishlist')
      .delete()
      .eq('id', wishlistItemId);

    if (error) {
      console.error('Error removing from wishlist:', error);
      return { error };
    }

    return { error: null };
  } catch (err) {
    console.error('Unexpected error in removeFromWishlist:', err);
    return { error: err };
  }
}

/**
 * Get creator's wishlist
 * @param creatorId - The creator's seller_profile ID
 */
export async function getCreatorWishlist(
  creatorId: string
): Promise<{ data: WishlistItem[] | null; error: any }> {
  try {
    const { data: wishlist, error: wishlistError } = await supabase
      .from('affiliate_wishlist')
      .select('*')
      .eq('creator_id', creatorId)
      .order('added_at', { ascending: false });

    if (wishlistError) {
      console.error('Error fetching wishlist:', wishlistError);
      return { data: null, error: wishlistError };
    }

    if (!wishlist || wishlist.length === 0) {
      return { data: [], error: null };
    }

    // Fetch product info
    const productIds = wishlist.map((w) => w.product_id);

    const { data: products, error: productsError } = await supabase
      .from('products')
      .select('product_id, product_name, price, main_images, seller_id')
      .in('product_id', productIds);

    // Fetch commission info and affiliate settings from product_affiliate_settings
    const { data: commissionSettings, error: commissionError } = await supabase
      .from('product_affiliate_settings')
      .select('product_id, commission_rate, commission_type, start_date, end_date, max_uses, current_uses')
      .eq('is_public', true)
      .eq('status', 'approved')
      .in('product_id', productIds);

    if (productsError || commissionError) {
      console.error('Error fetching products:', { productsError, commissionError });
    }

    // Create commission and affiliate settings map
    const commissionMap = new Map(
      (commissionSettings || []).map((s) => [s.product_id, {
        default_commission_rate: s.commission_rate,
        default_commission_type: s.commission_type,
        start_date: s.start_date,
        end_date: s.end_date,
        max_uses: s.max_uses,
        current_uses: s.current_uses
      }])
    );

    // Create map for quick lookup and combine with commission and affiliate data
    const productsMap = new Map(
      (products || []).map((p) => [
        p.product_id,
        {
          ...p,
          default_commission_rate: commissionMap.get(p.product_id)?.default_commission_rate || 10,
          default_commission_type: commissionMap.get(p.product_id)?.default_commission_type || 'percentage',
          affiliate_start_date: commissionMap.get(p.product_id)?.start_date,
          affiliate_end_date: commissionMap.get(p.product_id)?.end_date,
          affiliate_max_uses: commissionMap.get(p.product_id)?.max_uses,
          affiliate_current_uses: commissionMap.get(p.product_id)?.current_uses
        }
      ])
    );

    // Combine data
    const combinedWishlist: WishlistItem[] = wishlist.map((item) => ({
      ...item,
      product: productsMap.get(item.product_id),
    }));

    return { data: combinedWishlist, error: null };
  } catch (err) {
    console.error('Unexpected error in getCreatorWishlist:', err);
    return { data: null, error: err };
  }
}

/**
 * Check if product is in creator's wishlist
 * @param creatorId - The creator's seller_profile ID
 * @param productId - The product ID
 */
export async function isInWishlist(
  creatorId: string,
  productId: number
): Promise<{ isInWishlist: boolean; wishlistItemId?: string; error: any }> {
  try {
    const { data, error } = await supabase
      .from('affiliate_wishlist')
      .select('id')
      .eq('creator_id', creatorId)
      .eq('product_id', productId)
      .limit(1);

    if (error) {
      console.error('Error checking wishlist:', error);
      return { isInWishlist: false, error };
    }

    return {
      isInWishlist: data && data.length > 0,
      wishlistItemId: data?.[0]?.id,
      error: null,
    };
  } catch (err) {
    console.error('Unexpected error in isInWishlist:', err);
    return { isInWishlist: false, error: err };
  }
}
